/**
 * EWSMetricCard - Reusable metric display card
 *
 * Used to display aggregate metrics like Total Customers, Average Balance, etc.
 * Features a title, large value, optional icon, and decorative background.
 */

import React, { useState } from 'react';
import { Info } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { EWSMetricCardProps } from '../../../types/ews.types';

export const EWSMetricCard: React.FC<EWSMetricCardProps> = ({
  title,
  value,
  icon,
  backgroundImage,
  infoTooltip,
}) => {
  // Tooltip state for hybrid behavior (hover on desktop, tap on mobile)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        minHeight: '140px',
      }}
    >
      {/* Content at top */}
      <div className="p-4 relative z-10 h-full flex flex-col justify-between">
        {/* Header with title and info icon */}
        <div className="flex items-center gap-1.5 mb-3">
          <span className="text-sm font-medium text-gray-700">
            {title}
          </span>
          {infoTooltip && (
            <Tooltip.Provider delayDuration={200}>
              <Tooltip.Root
                open={isTooltipOpen}
                onOpenChange={setIsTooltipOpen}
              >
                <Tooltip.Trigger asChild>
                  <button
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => {
                      // Toggle on tap for mobile devices
                      setIsTooltipOpen((prev) => !prev);
                    }}
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className="max-w-xs px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg z-50"
                    sideOffset={5}
                    onPointerDownOutside={() => setIsTooltipOpen(false)}
                  >
                    {infoTooltip}
                    <Tooltip.Arrow className="fill-gray-900" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          )}
        </div>

        {/* Value */}
        <div className="text-4xl font-bold text-gray-900">
          {value}
        </div>
      </div>

      {/* Decorative background image - positioned top right */}
      {backgroundImage && (
        <div className="absolute right-1 bottom-1">
          <img
            src={backgroundImage}
            alt=""
            className="w-24 h-24 object-contain"
            style={{ opacity: 0.65 }}
          />
        </div>
      )}

      {/* Icon placeholder for visual interest */}
      {icon && (
        <div className="absolute right-4 top-4 text-gray-200">
          {icon}
        </div>
      )}
    </div>
  );
};

/**
 * Specialized metric card for the drawer (smaller variant).
 */
export const EWSMetricCardSmall: React.FC<{
  title: string;
  value: string | number;
  showInfo?: boolean;
  backgroundImage?: string;
}> = ({ title, value, showInfo = false, backgroundImage }) => {
  return (
    <div
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(241, 245, 249, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        minHeight: '80px',
      }}
    >
      <div className="p-4 relative z-10 h-full flex flex-col justify-between">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-sm font-medium text-gray-600">
            {title}
          </span>
          {showInfo && (
            <Info className="w-3.5 h-3.5 text-gray-600" />
          )}
        </div>
        <div className="text-2xl font-bold text-gray-900">
          {value}
        </div>
      </div>
      {/* Decorative background image */}
      {backgroundImage && (
        <div className="absolute right-1 bottom-1">
          <img
            src={backgroundImage}
            alt=""
            className="w-20 h-20 object-contain"
            style={{ opacity: 0.65 }}
          />
        </div>
      )}
    </div>
  );
};

export default EWSMetricCard;
